/** Mock appointment data, ported from the handoff `appointments.jsx`. Used
 *  until the live appointment data source (services/providers/availability) is
 *  wired into <zipper-appointments>. */

/**
 * PLAT-5624: an appointment-type GROUP tag (`AppointmentTypeDto.groups`).
 *
 * Groups are JSON tags stamped onto each appointment type — there is no group
 * table. They are matched by **`title`, never `id`**: the dashboard's
 * `AppointmentTypeGroupTags` discards the suggestion's id and mints a fresh
 * `uuid()` on every add, so "Wellness" carries a DIFFERENT id on every
 * provider's copy of the type. Every shipping consumer (schedule.service,
 * ZipperSchedulingBlockRevamp, MultisiteSchedulingProvider) matches on title,
 * and so do we.
 */
export interface AppointmentGroupTag {
  /** Per-provider uuid. Deliberately NOT used for matching — see above. */
  id?: string;
  title: string;
  orgId?: number;
}

export interface AppointmentService {
  id: string;
  /** Exact config/provider/type row identity used only by the client view model. */
  offeringKey?: string;
  scheduleConfigurationId?: string;
  scheduleId?: string;
  scheduleLabel?: string;
  sharedAppointmentTypeId?: string;
  sharedAppointmentTypeDurationId?: string;
  sharedAppointmentTypeRevision?: number;
  category: string;
  name: string;
  duration: number; // minutes
  price: number;
  description: string;
  thumbnail?: string;
  /**
   * PLAT-5624: the group tags this appointment type carries. Drives the
   * step-1 merge (one "Wellness" card standing in for every provider's copy)
   * — see `components/appointment-groups.ts`. Absent/empty = ungrouped, which
   * still renders as a normal, directly-bookable service row.
   */
  groups?: AppointmentGroupTag[];
  /**
   * PLAT-5624: the provider (user id) whose `ScheduleConfiguration` this type
   * came from. Appointment types live one-row-per-(org, provider), so a type
   * belongs to exactly one provider and carries THAT provider's price,
   * duration and `stripeProductId`.
   */
  providerUserId?: number;
  /** Real Stripe product id for the drop-in charge (live data; empty in mock). */
  stripeProductId?: string;
  /**
   * PLAT-5624: `AppointmentTypeDto.isFree` — the org's explicit "this costs
   * nothing" switch. WITHOUT it a `price` of 0 is ambiguous: it is equally the
   * shape of a genuinely free service AND of a credit-priced one (whose cost
   * lives on a credit-pack product, not on the appointment type). The embed used
   * to read only `price`, so a credit-priced service rendered as "Free" and was
   * bookable for $0. Never infer free-ness from `price` alone.
   */
  isFree?: boolean;
  /**
   * Legacy single form id (`AppointmentTypeDto.formId`). Kept only as a
   * fallback: the API migrates it into `forms` and zeroes it on the way out,
   * so it reads 0 on virtually every live type. Prefer `forms`.
   */
  formId?: number;
  /**
   * Entity forms (waiver / intake / cancelation-agreement) attached to this
   * appointment type, mirroring `classes[].forms` on the schedule. This is
   * where the org's real forms live — `AppointmentTypeDto`'s constructor moves
   * a legacy `formId` in here (as a waiver) and sets `formId = 0`.
   */
  forms?: { formId: number; type: string; requirement?: string }[];
  /** Max people per slot (AppointmentTypeDto.allowedParticipants). */
  allowedParticipants?: number;
  /** When true and the slot is empty, customers can buy out the whole session. */
  allowBuyout?: boolean;
  /** Flat private-session price in major currency units. */
  buyoutPrice?: number | null;
  /** Org ISO-4217 currency for buyoutPrice. */
  buyoutCurrency?: string;
}

export interface AppointmentProvider {
  id: string;
  name: string;
  gender?: 'F' | 'M';
  generic?: boolean;
  photo?: string;
  bio?: string;
}

export interface DaySlots {
  morning: string[];
  afternoon: string[];
  evening: string[];
}

export const MOCK_SERVICES: AppointmentService[] = [
  { id: 'normatec', category: 'Recovery', name: 'Normatec Boots', duration: 30, price: 45, description: 'Sequential compression boots that flush the legs, knees, and hips. Settle in with a podcast — the system runs through five zones across the session.', thumbnail: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?auto=format&fit=crop&w=480&h=320&q=80' },
  { id: 'sauna', category: 'Recovery', name: 'Infrared Sauna', duration: 30, price: 35, description: 'A single-person infrared cabin at 140°F. Recommended after lifting or a long run. Towels, water, and cold rinse included.', thumbnail: 'https://images.unsplash.com/photo-1554504318-1bbb8d4e0470?auto=format&fit=crop&w=480&h=320&q=80' },
  { id: 'cold-plunge', category: 'Recovery', name: 'Cold Plunge', duration: 15, price: 25, description: 'A guided plunge in a 50°F tub. Three rounds with breathwork between — built for nervous-system reset, not endurance.', thumbnail: 'https://images.unsplash.com/photo-1530210124550-912dc1381cb8?auto=format&fit=crop&w=480&h=320&q=80' },
  { id: 'red-light', category: 'Recovery', name: 'Red Light Therapy', duration: 20, price: 30, description: 'Full-body panel session combining red and near-infrared wavelengths. Quiet, warm, and easy to layer onto another service.', thumbnail: 'https://images.unsplash.com/photo-1540206395-68808572332f?auto=format&fit=crop&w=480&h=320&q=80' },
  { id: 'swedish-45', category: 'Massage', name: 'Swedish Massage', duration: 45, price: 95, description: 'A flowing, full-body relaxation massage. Medium pressure throughout, with extra attention on the neck, shoulders, and lower back.', thumbnail: 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?auto=format&fit=crop&w=480&h=320&q=80' },
  { id: 'deep-tissue-45', category: 'Massage', name: 'Deep Tissue Massage', duration: 45, price: 110, description: 'Targeted, firm pressure on chronically tight areas. Best when you can name the two or three spots that need work going in.', thumbnail: 'https://images.unsplash.com/photo-1519823551278-64ac92734fb1?auto=format&fit=crop&w=480&h=320&q=80' },
  { id: 'swedish-60', category: 'Massage', name: 'Swedish Massage', duration: 60, price: 120, allowBuyout: true, buyoutPrice: 280, description: 'The long version of our most-booked service. The extra fifteen minutes goes to a slower opening sequence and a deeper close.', thumbnail: 'https://images.unsplash.com/photo-1600334129128-685c5582fd35?auto=format&fit=crop&w=480&h=320&q=80' },
];

export const MOCK_PROVIDERS: AppointmentProvider[] = [
  { id: 'any', name: 'First Available', generic: true, bio: "We'll match you with the earliest instructor who has the slot you pick." },
  { id: 'marisol', name: 'Marisol Pena', gender: 'F', photo: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=200&h=200&q=80', bio: 'STOTT-certified instructor with 12 years across NYC studios.' },
  { id: 'lena', name: 'Lena Ortiz', gender: 'F', photo: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=200&h=200&q=80', bio: 'Halcyon founder. Specializes in privates and pre/post-natal work.' },
  { id: 'yasmin', name: 'Yasmin Castillo', gender: 'F', photo: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=200&h=200&q=80', bio: 'Athletic programming and post-rehab work. Bring a towel.' },
  { id: 'dylan', name: 'Dylan Greene', gender: 'M', photo: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=200&h=200&q=80', bio: 'Sports-oriented work, strong on programming for runners and lifters.' },
  { id: 'peter', name: 'Peter Lim', gender: 'M', photo: 'https://images.unsplash.com/photo-1463453091185-61582044d556?auto=format&fit=crop&w=200&h=200&q=80', bio: 'Classical lineage with clinical bodywork. Great for first-timers.' },
];

const ALL_SLOTS: DaySlots = {
  morning: ['9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM'],
  afternoon: ['12:30 PM', '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM', '3:00 PM', '4:00 PM'],
  evening: ['5:30 PM', '6:00 PM', '6:30 PM', '7:00 PM'],
};

function hash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

/** Deterministic mock availability for a provider on a date. */
export function slotsForProviderDay(providerId: string, date: Date): DaySlots {
  const dow = date.getDay();
  const out: DaySlots = { morning: [], afternoon: [], evening: [] };
  const baseRate = (hash(`${providerId}|${date.toDateString()}`) % 100) / 100;
  const dowMult = dow === 0 ? 0.15 : dow === 6 ? 0.45 : 0.75;
  const isGeneric = providerId.startsWith('any');
  const target = isGeneric ? Math.min(0.95, baseRate * 1.6) : baseRate * dowMult;
  (['morning', 'afternoon', 'evening'] as const).forEach((part) => {
    ALL_SLOTS[part].forEach((slot) => {
      const want = (hash(`${providerId}|${date.toDateString()}|${slot}`) % 1000) / 1000;
      if (want < target) out[part].push(slot);
    });
  });
  return out;
}

export function totalSlots(s: DaySlots): number {
  return s.morning.length + s.afternoon.length + s.evening.length;
}

export function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

/** First date with availability, today inclusive (scans 30 days ahead). */
export function firstAvailableDate(providerId: string, from: Date): Date | null {
  for (let i = 0; i <= 30; i++) {
    const d = addDays(from, i);
    if (totalSlots(slotsForProviderDay(providerId, d)) > 0) return d;
  }
  return null;
}
